import React, { useContext, useState, useMemo } from 'react';
import { toastr } from 'react-redux-toastr';
import {
  Box,
  Stack,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import DocumentTitle from 'react-document-title';
import {
  useLeaseApplicationForm,
  useApplicationFormReactHookFormProps,
} from '../hooks';
import { AppContext } from '../../App/context';
import ApplicationFormHeader from './ApplicationFormHeader';
import ApplicationFormContainer from './ApplicationFormContainer';
import type { LeaseApplicationFormProps } from './types';
import useBodyOverflowHidden from '../../../hooks/useBodyOverflowHidden';
import useHistory from '../../../hooks/useHistory';
import SaveAndCloseModal from './SaveAndCloseModal';
import UploadApplicationModal from './UploadApplicationModal';
import { APPLICATION_FORM_ID } from '../constants';
import { mapFormValuesToApplicantData } from '../utils/formMappers';
import revealBlockingFields, {
  BLOCKING_FIELDS_MESSAGE,
} from '../utils/revealBlockingFields';
import { post, postDownload } from '../../../utils/api';
import { getCookie } from '../../../utils/cookie';
import SendForSignatureModal from './SendForSignatureModal';
import moment from 'moment';

const LeaseApplicationForm = ({ match }: LeaseApplicationFormProps) => {
  const history = useHistory();
  const { selectedProperty } = useContext(AppContext);
  const { applicantId, applicationId } = match.params;

  const canSignElectronically = useMemo(() => {
    return (
      selectedProperty?.isResidentPortalActive &&
      selectedProperty?.isElectronicLeaseSigningActive
    );
  }, [
    selectedProperty?.isResidentPortalActive,
    selectedProperty?.isElectronicLeaseSigningActive,
  ]);

  const {
    initialValues,
    asyncOptions,
    isLoading,
    schema,
    employmentStatuses,
    alreadySentForSignature,
    isPendingResidentApplicant,
    isAffordable,
  } = useLeaseApplicationForm({
    applicantId,
    selectedProperty,
    applicationId,
    canSignElectronically,
  });

  const ReactHookFormProps = useApplicationFormReactHookFormProps({
    initialValues,
    applicantId,
    applicationId,
    isLoading,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSaveAndCloseModalOpen, setIsSaveAndCloseModalOpen] = useState(false);
  const [isUploadApplicationModalOpen, setIsUploadApplicationModalOpen] =
    useState(false);
  const [isSendForSignatureModalOpen, setIsSendForSignatureModalOpen] =
    useState(false);

  const handleOpenUploadApplicationModal = () => {
    setIsUploadApplicationModalOpen(true);
  };

  const handleCloseUploadApplicationModal = () => {
    setIsUploadApplicationModalOpen(false);
  };

  useBodyOverflowHidden();

  const closeForm = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      // Navigate to applicant profile
      history.push(
        `/property/${selectedProperty.id}/application/${applicationId}`,
      );
    }
  };

  const handleCancel = () => {
    closeForm();
  };

  const handleOpenSaveAndCloseModal = () => {
    setIsSaveAndCloseModalOpen(true);
  };

  const handlePrint = async () => {
    if (!selectedProperty) return;

    setIsPrinting(true);

    const formValues = ReactHookFormProps.getValues();
    const data = mapFormValuesToApplicantData({
      formValues,
      employmentStatuses,
      initialValues,
    });

    const { firstName, lastName } = data.applicantInformation.basicInformation;
    const fileName = `${firstName || 'Applicant'}-${
      lastName || ''
    }-Application-Form-${moment().format('YYYY-MM-DD-HHmmss')}.pdf`;

    try {
      await postDownload(
        `/${selectedProperty.organizationId}/${selectedProperty.id}/applicant/${applicantId}/applicant-form/pdf`,
        fileName,
        JSON.stringify({
          data,
          includeIncomeAndAssets: isAffordable,
          includeTermsAndConditions: true,
        }),
      );
    } catch (error) {
      toastr.error(
        'Error',
        error?.message ||
          error?.networkError ||
          'Failed to download application PDF.',
      );
    } finally {
      setIsPrinting(false);
    }
  };

  const handleSaveAndClose = () => {
    const form = document.getElementById(APPLICATION_FORM_ID);

    if (!form) return;

    setIsSaving(true);
    setIsSaveAndCloseModalOpen(false);

    form.requestSubmit();
  };

  const saveFormAPICall = async (applicantFormData) => {
    return post(
      `/${selectedProperty.organizationId}/${selectedProperty.id}/applicant/${applicantId}/applicant-form`,
      JSON.stringify(applicantFormData),
    );
  };

  const handleSubmit = async (values) => {
    if (!values.legalFirstName || !values.lastName || !values.phoneNumber) {
      toastr.error(
        'Error',
        'Please make sure you have entered your first, last name, and phone number.',
      );
      setIsSaving(false);
      return;
    }

    const applicantFormData = mapFormValuesToApplicantData({
      formValues: values,
      employmentStatuses,
      initialValues,
    });

    try {
      await saveFormAPICall(applicantFormData);
      closeForm();
    } catch {
      toastr.error('Error', 'Failed to save application.');
    } finally {
      setIsSaving(false);
    }
  };

  const completeForm = async ({ signingMethod, applicationFile }) => {
    setIsCompleting(true);

    try {
      const applicantFormData = mapFormValuesToApplicantData({
        formValues: ReactHookFormProps.getValues(),
        employmentStatuses,
        initialValues,
      });

      await saveFormAPICall(applicantFormData);

      const formData = new FormData();

      if (signingMethod === 'manual' && applicationFile) {
        formData.append('applicationFile', applicationFile);
      }

      await post(
        `/${selectedProperty.organizationId}/${selectedProperty.id}/applicant/${applicantId}/applicant-form/complete`,
        formData,
        {
          headers: {
            Authorization: getCookie('session_id'),
          },
        },
      );

      closeForm();
    } catch {
      toastr.error('Error', 'Failed to complete application.');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleUploadApplication = async (file) => {
    handleCloseUploadApplicationModal();
    await completeForm({
      signingMethod: 'manual',
      applicationFile: file,
    });
  };

  const handleSendForSignature = async () => {
    handleCloseSendForSignatureModal();
    await completeForm({
      signingMethod: 'electronic',
    });
  };

  const handleOpenSendForSignatureModal = () => {
    setIsSendForSignatureModalOpen(true);
  };

  const handleCloseSendForSignatureModal = () => {
    setIsSendForSignatureModalOpen(false);
  };

  const handleRevealBlockingFields = () =>
    revealBlockingFields({
      trigger: ReactHookFormProps.trigger,
      onInvalid: () => toastr.error('Error', BLOCKING_FIELDS_MESSAGE),
    });

  const selectedSigningOption = ReactHookFormProps.watch('signingMethod');

  return (
    <DocumentTitle title="Application Form">
      <Stack
        alignItems="center"
        width="100%"
        height="100vh"
        sx={{ overflow: 'hidden' }}
      >
        <ApplicationFormHeader
          propertyName={selectedProperty?.name}
          applicationId={applicationId}
          propertyId={selectedProperty?.id}
          isLoading={isLoading}
          isSaving={isSaving}
          isCompleting={isCompleting}
          isPrinting={isPrinting}
          onCancel={handleCancel}
          onSaveAndClose={handleOpenSaveAndCloseModal}
          onUploadApplication={handleOpenUploadApplicationModal}
          onSendForSignature={handleOpenSendForSignatureModal}
          canSignElectronically={canSignElectronically}
          selectedSigningOption={selectedSigningOption}
          isFormValid={ReactHookFormProps.isValid}
          onRevealBlockingFields={handleRevealBlockingFields}
          alreadySentForSignature={alreadySentForSignature}
          isPendingResidentApplicant={isPendingResidentApplicant}
          onPrint={handlePrint}
        />
        <Box
          sx={{
            backgroundColor: 'background.paper',
            padding: 4,
            paddingTop: 6,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            width: {
              xs: '100%',
              sm: '100%',
              md: '90%',
              lg: '80%',
              xl: '70%',
            },
          }}
        >
          <ApplicationFormContainer
            ReactHookFormProps={ReactHookFormProps}
            asyncOptions={asyncOptions}
            isLoading={isLoading}
            schema={schema}
            onSubmit={handleSubmit}
          />
        </Box>
        <SaveAndCloseModal
          open={isSaveAndCloseModalOpen}
          onClose={() => setIsSaveAndCloseModalOpen(false)}
          onSaveAndClose={handleSaveAndClose}
        />
        <UploadApplicationModal
          open={isUploadApplicationModalOpen}
          onClose={handleCloseUploadApplicationModal}
          onUploadApplication={handleUploadApplication}
        />
        <SendForSignatureModal
          open={isSendForSignatureModalOpen}
          onClose={handleCloseSendForSignatureModal}
          onSendForSignature={handleSendForSignature}
        />
      </Stack>
    </DocumentTitle>
  );
};

export default LeaseApplicationForm;
